import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { violationCategoriesService } from '../../../services/supabaseService';

const ViolationCategoriesSection = () => {
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    severity: '',
    points: '',
    escalation: false
  });

  // Load violation categories from Supabase on component mount
  useEffect(() => {
    loadViolations();
  }, []);

  const loadViolations = async () => {
    try {
      setLoading(true);
      const data = await violationCategoriesService?.getAll();
      setViolations(data);
    } catch (error) {
      console.error('Error loading violation categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { value: 'Attendance', label: 'Attendance & Punctuality' },
    { value: 'Conduct', label: 'Workplace Conduct' },
    { value: 'Performance', label: 'Performance & Quality' },
    { value: 'Administrative', label: 'Administrative' }
  ];

  const severityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Critical', label: 'Critical' }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      'Attendance': 'bg-blue-100 text-blue-700',
      'Conduct': 'bg-purple-100 text-purple-700',
      'Performance': 'bg-orange-100 text-orange-700',
      'Administrative': 'bg-green-100 text-green-700'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-700';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'Low': 'text-success',
      'Medium': 'text-warning',
      'High': 'text-error',
      'Critical': 'text-destructive'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const handleEdit = (violation) => {
    setEditingId(violation?.id);
    setFormData({
      name: violation?.name,
      category: violation?.category,
      severity: violation?.severity,
      points: violation?.points,
      escalation: violation?.escalation
    });
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        const updatedViolation = await violationCategoriesService?.update(editingId, {
          ...formData,
          points: parseFloat(formData?.points)
        });
        if (updatedViolation) {
          setViolations(prev => prev?.map(v => v?.id === editingId ? updatedViolation : v));
          setEditingId(null);
        }
      } else {
        const newViolation = await violationCategoriesService?.create({
          ...formData,
          points: parseFloat(formData?.points)
        });
        if (newViolation) {
          setViolations(prev => [...prev, newViolation]);
          setShowAddForm(false);
        }
      }
      setFormData({ name: '', category: '', severity: '', points: '', escalation: false });
    } catch (error) {
      console.error('Error saving violation category:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const success = await violationCategoriesService?.delete(id);
      if (success) {
        setViolations(prev => prev?.filter(v => v?.id !== id));
      }
    } catch (error) {
      console.error('Error deleting violation category:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({ name: '', category: '', severity: '', points: '', escalation: false });
  };

  const groupedViolations = violations?.reduce((acc, violation) => {
    if (!acc?.[violation?.category]) {
      acc[violation?.category] = [];
    }
    acc?.[violation?.category]?.push(violation);
    return acc;
  }, {});

  return (
    <div className="p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading violation categories...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading font-semibold text-xl text-foreground mb-1">
                Violation Categories
              </h2>
              <p className="font-body text-sm text-muted-foreground">
                Manage violation types, severity levels, and default point values
              </p>
            </div>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowAddForm(true)}
            >
              Add Violation
            </Button>
          </div>

          {showAddForm && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">
                Add New Violation Type
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Violation Name"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  placeholder="Enter violation name"
                />
                <Select
                  label="Category"
                  options={categoryOptions}
                  value={formData?.category}
                  onChange={(value) => setFormData({ ...formData, category: value })}
                  placeholder="Select category"
                />
                <Select
                  label="Severity Level"
                  options={severityOptions}
                  value={formData?.severity}
                  onChange={(value) => setFormData({ ...formData, severity: value })}
                  placeholder="Select severity"
                />
                <Input
                  label="Default Points"
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData?.points}
                  onChange={(e) => setFormData({ ...formData, points: e?.target?.value })}
                  placeholder="0.0"
                />
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  id="escalation"
                  checked={formData?.escalation}
                  onChange={(e) => setFormData({ ...formData, escalation: e?.target?.checked })}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="escalation" className="text-sm text-foreground">
                  Enable automatic escalation trigger
                </label>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="default" onClick={handleSave}>
                  Add Violation
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {Object?.entries(groupedViolations)?.map(([category, categoryViolations]) => (
              <div key={category}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(category)}`}>
                    {category}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">
                    {categoryViolations?.length} violations
                  </span>
                </div>

                <div className="space-y-2">
                  {categoryViolations?.map((violation) => (
                    <div
                      key={violation?.id}
                      className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-smooth"
                    >
                      {editingId === violation?.id ? (
                        <div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <Input
                              label="Violation Name"
                              value={formData?.name}
                              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                            />
                            <Select
                              label="Category"
                              options={categoryOptions}
                              value={formData?.category}
                              onChange={(value) => setFormData({ ...formData, category: value })}
                            />
                            <Select
                              label="Severity Level"
                              options={severityOptions}
                              value={formData?.severity}
                              onChange={(value) => setFormData({ ...formData, severity: value })}
                            />
                            <Input
                              label="Default Points"
                              type="number"
                              step="0.5"
                              min="0"
                              value={formData?.points}
                              onChange={(e) => setFormData({ ...formData, points: e?.target?.value })}
                            />
                          </div>
                          <div className="flex items-center gap-2 mb-4">
                            <input
                              type="checkbox"
                              id={`escalation-${violation?.id}`}
                              checked={formData?.escalation}
                              onChange={(e) => setFormData({ ...formData, escalation: e?.target?.checked })}
                              className="h-4 w-4 rounded border-input"
                            />
                            <label htmlFor={`escalation-${violation?.id}`} className="text-sm text-foreground">
                              Enable automatic escalation trigger
                            </label>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button variant="default" size="sm" onClick={handleSave}>
                              Save Changes
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-heading font-semibold text-base text-foreground">
                                {violation?.name}
                              </h4>
                              <span className={`text-xs font-semibold ${getSeverityColor(violation?.severity)}`}>
                                {violation?.severity}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-muted-foreground">
                                Points: <span className="font-data font-semibold text-error">{violation?.points}</span>
                              </span>
                              {violation?.escalation && (
                                <span className="flex items-center gap-1 text-warning">
                                  <Icon name="AlertTriangle" size={14} />
                                  <span className="text-xs">Auto-escalation enabled</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Edit2"
                              onClick={() => handleEdit(violation)}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Trash2"
                              onClick={() => handleDelete(violation?.id)}
                              className="text-destructive hover:text-destructive"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ViolationCategoriesSection;